/* eslint-disable max-lines */

/* Sorting
 *
 * - Public API
 *   - action order from public api documentation
 *   - action property order from public api documentation
 * - Internal API
 *   - action order from internal api documentation
 *   - action property order from internal api documentation
 * - Undocumented API
 *   - action order alphabetically
 *   - action property order
 *     - id properties first alphabetically
 *     - rest of properties alphabetically
 */

import {
    NinchatAttributes,
    NinchatMessages,
    NinchatProperties,
    NinchatSettings,
} from './'
import {NinchatAudienceMetadata} from './properties'
import {NinchatRealmSettings} from './settings'

export enum NinchatActionType {
    // Public API
    NinchatCreateSessionAction = 'create_session',
    NinchatResumeSessionAction = 'resume_session',
    NinchatUpdateSessionAction = 'update_session',
    NinchatCloseSessionAction = 'close_session',
    NinchatCreateUserAction = 'create_user',
    NinchatDescribeUserAction = 'describe_user',
    NinchatUpdateUserAction = 'update_user',
    NinchatDeleteUserAction = 'delete_user',
    NinchatCreateIdentityAction = 'create_identity',
    NinchatCreateIdentityWithAuthResetAccessAction = 'create_identity_with_auth_reset_access',
    NinchatRequestIdentityVerifyAccessAction = 'request_identity_verify_access',
    NinchatVerifyIdentityAction = 'verify_identity',
    NinchatDescribeIdentityAction = 'describe_identity',
    NinchatUpdateIdentityAction = 'update_identity',
    NinchatUpdateIdentityAuthAction = 'update_identity_auth',
    NinchatRequestIdentityAuthResetAccessAction = 'request_identity_auth_reset_access',
    NinchatResetIdentityAuthAction = 'reset_identity_auth',
    NinchatDeleteIdentityAction = 'delete_identity',
    NinchatUpdateDialogueAction = 'update_dialogue',
    NinchatCreateChannelAction = 'create_channel',
    NinchatDescribeChannelAction = 'describe_channel',
    NinchatUpdateChannelAction = 'update_channel',
    NinchatDeleteChannelAction = 'delete_channel',
    NinchatFollowChannelAction = 'follow_channel',
    NinchatJoinChannelAction = 'join_channel',
    NinchatPartChannelAction = 'part_channel',
    NinchatCreateRealmAction = 'create_realm',
    NinchatDescribeRealmAction = 'describe_realm',
    NinchatDescribeRealmQueuesAction = 'describe_realm_queues',
    NinchatDescribeRealmSitesAction = 'describe_realm_sites',
    NinchatUpdateRealmAction = 'update_realm',
    NinchatDeleteRealmAction = 'delete_realm',
    NinchatCreateQueueAction = 'create_queue',
    NinchatUpdateQueueAction = 'update_queue',
    NinchatDeleteQueueAction = 'delete_queue',
    NinchatDescribeQueueAction = 'describe_queue',
    NinchatDescribeSiteAction = 'describe_site',
    NinchatDeleteSiteAction = 'delete_site',
    NinchatUpdateSiteAction = 'update_site',
    NinchatCreateSiteAction = 'create_site',
    NinchatRequestAudienceAction = 'request_audience',
    NinchatAcceptAudienceAction = 'accept_audience',
    NinchatTransferAudienceAction = 'transfer_audience',
    NinchatAddMemberAction = 'add_member',
    NinchatUpdateMemberAction = 'update_member',
    NinchatRemoveMemberAction = 'remove_member',
    NinchatSendMessageAction = 'send_message',
    NinchatUpdateMessageAction = 'update_message',
    NinchatUpdateUserMessagesAction = 'update_user_messages',
    NinchatLoadHistoryAction = 'load_history',
    NinchatDiscardHistoryAction = 'discard_history',
    NinchatCreateAccessAction = 'create_access',
    NinchatSendAccessAction = 'send_access',
    NinchatDescribeAccessAction = 'describe_access',
    NinchatDescribeMasterKeysAction = 'describe_master_keys',
    NinchatCreateMasterKeyAction = 'create_master_key',
    NinchatDeleteMasterKeyAction = 'delete_master_key',
    NinchatSendFileAction = 'send_file',
    NinchatSendFileUnlinkedAction = 'send_file_unlinked',
    NinchatRemoveFileUnlinkedAction = 'remove_file',
    NinchatDescribeFileAction = 'describe_file',
    NinchatGetTranscriptAction = 'get_transcript',
    NinchatDeleteTranscriptAction = 'delete_transcript',
    NinchatDescribeQueueTranscriptsAction = 'describe_queue_transcripts',
    NinchatDeleteQueueTranscriptsAction = 'delete_queue_transcripts',
    NinchatCreateTagAction = 'create_tag',
    NinchatDescribeTagAction = 'describe_tag',
    NinchatDescribeTagsAction = 'describe_tags',
    NinchatUpdateTagAction = 'update_tag',
    NinchatDeleteTagAction = 'delete_tag',
    NinchatGetQueueStatsAction = 'get_queue_stats',
    NinchatGetQueueMetadataAction = 'get_queue_metadata',
    NinchatBeginIceAction = 'begin_ice',
    NinchatSearchAction = 'search',
    NinchatSearchUsersAction = 'search_users',
    NinchatSearchChannelsAction = 'search_channels',
    NinchatTrackAction = 'track',
    NinchatPingAction = 'ping',
    // Internal API
    NinchatDeleteMessageAction = 'delete_message',
    NinchatRegisterAudienceAction = 'register_audience',
    NinchatEndAudienceAction = 'end_audience',
    NinchatCreateCommentAction = 'create_comment',
    NinchatGetAudienceRangeCommentsAction = 'get_audience_range_comments',
    NinchatBeginSsoAction = 'begin_sso',
    // Undocumented API
    NinchatAssignOldestTranscriptAction = 'assign_oldest_transcript',
    NinchatAssignTranscriptAction = 'assign_transcript',
    NinchatBlockAudienceAddressAction = 'block_audience_address',
    NinchatDescribeTranscriptAction = 'describe_transcript',
    NinchatDiscoverJitsiAction = 'discover_jitsi',
    NinchatGetQueueActivityAction = 'get_queue_activity',
    NinchatSetTranscriptStatusAction = 'set_transcript_status',
    NinchatGetPermissionGroupsAction = 'list_permission_groups',
    NinchatCreatePermissionGroupAction = 'create_permission_group',
    NinchatUpdatePermissionGroupAction = 'update_permission_group',
    NinchatDeletePermissionGroupAction = 'delete_permission_group',
    NinchatJoinPermissionGroupAction = 'join_permission_group',
}

/* Base */

interface NinchatPuppetActions {
    puppet_id?: NinchatProperties.UserID
}

/* Actions */

/* Public API
 * - action order from public api documentation
 * - action property order from public api documentation
 */

export interface NinchatCreateSessionAction {
    action: 'create_session'
    features?: string
    action_id: NinchatProperties.ActionID
    user_id?: NinchatProperties.UserID
    user_auth?: NinchatProperties.UserAuth
    user_attrs?: NinchatAttributes.NinchatUserAttributes
    user_settings?: NinchatSettings.NinchatUserSettings
    user_metadata?: NinchatProperties.Metadata
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
    identity_auth?: NinchatProperties.IdentityAuth
    identity_type_new?: NinchatProperties.IdentityType
    identity_name_new?: NinchatProperties.IdentityName
    identity_auth_new?: NinchatProperties.IdentityAuth
    identity_attrs?: NinchatAttributes.NinchatIdentityAttributes
    access_key?: NinchatProperties.AccessKey
    master_key_type?: NinchatProperties.MasterKeyType
    master_sign?: NinchatProperties.MasterSign
    puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes
    message_types: NinchatMessages.NinchatMessageType[]
    metadata?: {
        client_instance?: number
    }
    user_otp?: number // undocumented
}

// export interface NinchatResumeSessionAction {}

export interface NinchatUpdateSessionAction {
    action: 'update_session'
    session_idle?: boolean
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    message_id?: NinchatProperties.MessageID
}

export interface NinchatCloseSessionAction {
    action: 'close_session'
}

// export interface NinchatCreateUserAction {}

export interface NinchatDescribeUserAction extends NinchatPuppetActions {
    action: 'describe_user'
    action_id: NinchatProperties.ActionID
    user_id?: NinchatProperties.UserID
}

export interface NinchatUpdateUserAction extends NinchatPuppetActions {
    action: 'update_user'
    action_id: NinchatProperties.ActionID
    user_attrs?: NinchatAttributes.NinchatUserAttributes
    user_settings?: NinchatSettings.NinchatUserSettings
    payload_attrs?: NinchatAttributes.NinchatPayloadAttributes
}

export interface NinchatDeleteUserAction {
    action: 'delete_user'
    action_id: NinchatProperties.ActionID
    user_auth?: NinchatProperties.UserAuth
}

export interface NinchatCreateIdentityAction {
    action: 'create_identity'
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs?: NinchatAttributes.NinchatIdentityAttributes
    identity_auth_new?: NinchatProperties.IdentityAuth
}

export interface NinchatCreateIdentityWithAuthResetAccessAction {
    action: 'create_identity_with_auth_reset_access'
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs?: NinchatAttributes.NinchatIdentityAttributes
}

export interface NinchatRequestIdentityVerifyAccessAction {
    action: 'request_identity_verify_access',
    action_id?: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
}

export interface NinchatVerifyIdentityAction {
    action: 'verify_identity',
    action_id?: NinchatProperties.ActionID
    access_key: NinchatProperties.AccessKey
    identity_accept: boolean
}

export interface NinchatDescribeIdentityAction {
    action: 'describe_identity',
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
}

export interface NinchatUpdateIdentityAction {
    action: 'update_identity',
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs: NinchatAttributes.NinchatIdentityAttributes
}

export interface NinchatUpdateIdentityAuthAction {
    action: 'update_identity_auth',
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_auth?: NinchatProperties.IdentityAuth
    identity_auth_new?: NinchatProperties.IdentityAuth
}

export interface NinchatRequestIdentityAuthResetAccessAction {
    action: 'request_identity_auth_reset_access',
    action_id?: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
}

export interface NinchatResetIdentityAuthAction {
    action: 'reset_identity_auth',
    access_key: NinchatProperties.AccessKey
    identity_auth_new: NinchatProperties.IdentityAuth
}

export interface NinchatDeleteIdentityAction {
    action: 'delete_identity',
    action_id: NinchatProperties.ActionID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_auth?: NinchatProperties.IdentityAuth
}

export interface NinchatUpdateDialogueAction {
    action: 'update_dialogue'
    action_id: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    member_attrs?: NinchatAttributes.NinchatDialogueMemberAttributes
    dialogue_status?: NinchatProperties.DialogueStatus
}

export interface NinchatCreateChannelAction {
    action: 'create_channel'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    channel_attrs : NinchatAttributes.NinchatChannelAttributes
}

export interface NinchatDescribeChannelAction extends NinchatPuppetActions {
    action: 'describe_channel'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
}

export interface NinchatUpdateChannelAction extends NinchatPuppetActions {
    action: 'update_channel'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
}

// export interface NinchatDeleteChannelAction {}
// export interface NinchatFollowChannelAction {}

export interface NinchatJoinChannelAction extends NinchatPuppetActions {
    action: 'join_channel'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    access_key?: NinchatProperties.AccessKey
    master_key_type?: NinchatProperties.MasterKeyType
    master_sign?: NinchatProperties.MasterSign
    member_attrs?: Pick<NinchatAttributes.NinchatChannelMemberAttributes, 'silenced'>
}

export interface NinchatPartChannelAction extends NinchatPuppetActions {
    action: 'part_channel'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
}

// export interface NinchatCreateRealmAction {}

export interface NinchatDescribeRealmAction extends NinchatPuppetActions {
    action: 'describe_realm'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
}

export interface NinchatDescribeRealmQueuesAction {
    action: 'describe_realm_queues'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    queue_ids?: NinchatProperties.QueueID[]
    track_stage?: string
    track_metadata?: object
}

export interface NinchatDescribeRealmSitesAction {
    action: 'describe_realm_sites'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
}

export interface NinchatDeleteSiteAction {
    action: 'delete_site'
    action_id: NinchatProperties.ActionID
    site_id: NinchatProperties.SiteId
    realm_id: NinchatProperties.RealmID
}

export interface NinchatUpdateSiteAction {
    action: 'update_site'
    site_id: NinchatProperties.SiteId
    realm_id: NinchatProperties.RealmID
    site_attrs?: NinchatAttributes.NinchatSiteAttributes
    config?: string
}

export interface NinchatCreateSiteAction {
    action: 'create_site'
    realm_id: NinchatProperties.RealmID
    site_attrs?: NinchatAttributes.NinchatSiteAttributes
    config?: string
}

export interface NinchatUpdateRealmAction {
    action: 'update_realm'
    action_id : NinchatProperties.ActionID
    realm_id : NinchatProperties.RealmID
    realm_attrs? : NinchatAttributes.NinchatRealmAttributes
    realm_settings? : NinchatRealmSettings
}


// export interface NinchatDeleteRealmAction {}
export interface NinchatCreateQueueAction {
    action: 'create_queue'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    queue_attrs : NinchatAttributes.NinchatQueueAttributes
}

export interface NinchatUpdateQueueAction extends NinchatPuppetActions {
    action: 'update_queue'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_attrs?: NinchatAttributes.NinchatQueueAttributes
    queue_settings?: NinchatSettings.NinchatQueueSettings
}

export interface NinchatDeleteQueueAction {
    action: 'delete_queue'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    realm_id: NinchatProperties.RealmID
}

export interface NinchatDescribeQueueAction extends NinchatPuppetActions {
    action: 'describe_queue'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
}

export interface NinchatDescribeSiteAction extends NinchatPuppetActions {
    action: 'describe_site'
    action_id: NinchatProperties.ActionID
    site_id: NinchatProperties.QueueID
    realm_id: NinchatProperties.RealmID
}

export interface NinchatRequestAudienceAction {
    action: 'request_audience'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    file_ids?: NinchatProperties.FileID[]
    audience_metadata?: NinchatAudienceMetadata
}

export interface NinchatAcceptAudienceAction extends NinchatPuppetActions {
    action: 'accept_audience'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    audience_id?: NinchatProperties.AudienceID
}

export interface NinchatTransferAudienceAction extends NinchatPuppetActions {
    action: 'transfer_audience'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
    queue_id: NinchatProperties.QueueID
    transfer_no_part?: boolean // undocumented
}

export interface NinchatAddMemberAction {
    action: 'add_member'
    action_id: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    queue_id?: NinchatProperties.QueueID
    channel_id?: NinchatProperties.ChannelID
    user_id: NinchatProperties.UserID
    member_attrs?: NinchatAttributes.NinchatQueueMemberAttributes
}

export interface NinchatUpdateMemberAction extends NinchatPuppetActions {
    action: 'update_member'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    realm_id?: NinchatProperties.RealmID
    user_id: NinchatProperties.UserID
    member_attrs: NinchatAttributes.NinchatChannelMemberAttributes
    interval_end?: NinchatProperties.Time
}

export interface NinchatRemoveMemberAction extends NinchatPuppetActions {
    action: 'remove_member'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    realm_id?: NinchatProperties.RealmID
    queue_id?: NinchatProperties.QueueID
    user_id: NinchatProperties.UserID
}

export interface NinchatSendMessageAction extends NinchatPuppetActions {
    action: 'send_message'
    action_id?: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
    message_type: NinchatMessages.NinchatMessageType
    message_recipient_ids?: NinchatProperties.UserID[]
    message_fold?: boolean
    message_ttl?: NinchatProperties.Seconds
    message_answer?: NinchatProperties.NinchatMessageAnswer // internal
    message_privileged?: boolean // internal
}

export interface NinchatUpdateMessageAction {
    action: 'update_message'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    message_id: NinchatProperties.MessageID
    message_hidden: boolean
}

export interface NinchatUpdateUserMessagesAction {
    action: 'update_user_messages'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    message_user_id: NinchatProperties.UserID
    message_id?: NinchatProperties.MessageID
    interval_end?: NinchatProperties.Time
    message_hidden: boolean
}

export interface NinchatLoadHistoryAction extends NinchatPuppetActions {
    action: 'load_history'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    message_types?: NinchatMessages.NinchatMessageType[]
    message_id?: NinchatProperties.MessageID
    message_fold?: boolean
    history_length?: NinchatProperties.Count
    history_order?: NinchatProperties.HistoryOrder
    filter_property?: NinchatProperties.FilterProperty // documented not optional
    filter_substring?: NinchatProperties.FilterSubstring // documented not optional
}

// export interface NinchatDiscardHistoryAction {}

export interface NinchatCreateAccessAction extends NinchatPuppetActions {
    action: 'create_access'
    action_id: NinchatProperties.ActionID
    access_type: NinchatProperties.AccessType
    channel_id?: NinchatProperties.ChannelID
    channel_unsilence?: boolean
    realm_member?: boolean
    user_id?: NinchatProperties.UserID
}

export interface NinchatSendAccessAction {
    action: 'send_access'
    action_id: NinchatProperties.ActionID
    access_key?: NinchatProperties.AccessKey
    user_id?: NinchatProperties.UserID
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
}

export interface NinchatDescribeAccessAction extends NinchatPuppetActions {
    action: 'describe_access'
    action_id?: NinchatProperties.ActionID
    access_key: NinchatProperties.AccessKey
}

export interface NinchatDescribeMasterKeysAction {
    action: 'describe_master_keys'
    action_id?: NinchatProperties.ActionID
}

export interface NinchatCreateMasterKeyAction {
    action: 'create_master_key'
    action_id?: NinchatProperties.ActionID
    master_key_attrs?: NinchatProperties.MasterKeyAttrs
    master_key_id?: NinchatProperties.MasterKeyID
    master_key_type?: NinchatProperties.MasterKeyType
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatDeleteMasterKeyAction {
    action: 'delete_master_key'
    action_id?: NinchatProperties.ActionID
    master_key_id: NinchatProperties.MasterKeyID
    master_key_secret?: NinchatProperties.MasterKeySecret
    master_key_type?: NinchatProperties.MasterKeyType
    realm_id?: NinchatProperties.RealmID
    user_auth?: NinchatProperties.UserAuth
}

export interface NinchatSendFileAction extends NinchatPuppetActions {
    action: 'send_file'
    action_id: NinchatProperties.ActionID
    file_attrs: NinchatAttributes.NinchatFileAttributes
    user_id?: NinchatProperties.UserID
    channel_id?: NinchatProperties.ChannelID
}

export type NinchatSendFileUnlinkedAction = NinchatPuppetActions & Omit<NinchatSendFileAction, 'channel_id' | 'action'>
    & {action: 'send_file_unlinked'}

export interface NinchatRemoveFileUnlinkedAction extends NinchatPuppetActions {
    action: 'remove_file'
    action_id: NinchatProperties.ActionID
    file_id: NinchatProperties.FileID
}

export interface NinchatDescribeFileAction extends NinchatPuppetActions {
    action: 'describe_file'
    action_id: NinchatProperties.ActionID
    file_id: NinchatProperties.FileID
}

export interface NinchatGetTranscriptAction {
    action: 'get_transcript'
    action_id: NinchatProperties.ActionID
    action_reason?: string
    channel_id?: NinchatProperties.ChannelID
    dialogue_id?: NinchatProperties.UserID[]
    audience_id?: NinchatProperties.AudienceID
    interval_begin?: NinchatProperties.Time
    interval_end?: NinchatProperties.Time
    message_id?: NinchatProperties.MessageID
}

// export interface NinchatDeleteTranscriptAction {}

export interface NinchatDescribeQueueTranscriptsAction {
    action: 'describe_queue_transcripts'
    queue_id: NinchatProperties.QueueID
    interval_begin: NinchatProperties.Time
    interval_end?: NinchatProperties.Time
    interval_ongoing?: boolean
    search_metadata?: NinchatProperties.Metadata
}

// export interface NinchatDeleteQueueTranscriptsAction {}

export interface NinchatCreateTagAction {
    action: 'create_tag'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatDescribeTagAction {
    action: 'describe_tag'
    action_id: NinchatProperties.ActionID
    tag_id: NinchatProperties.TagID
}

export interface NinchatDescribeTagsAction extends NinchatPuppetActions {
    action: 'describe_tags'
    action_id: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id?: NinchatProperties.TagID
    tag_depth?: NinchatProperties.Count
}

export interface NinchatUpdateTagAction {
    action: 'update_tag'
    action_id: NinchatProperties.ActionID
    tag_id: NinchatProperties.TagID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatDeleteTagAction {
    action: 'delete_tag'
    action_id: NinchatProperties.ActionID
    tag_id: NinchatProperties.TagID
}

export interface NinchatGetQueueStatsAction {
    action: 'get_queue_stats'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    stats_flavor?: 'j' // undocumented
    stats_hour?: string
    stats_length: number
    with_user_attrs?: boolean // undocumented
}
// export interface NinchatBeginIceAction {}
// export interface NinchatSearchAction {}
// export interface NinchatSearchUsersAction {}
// export interface NinchatSearchChannelsAction {}
// export interface NinchatTrackAction {}
// export interface NinchatPingAction {}

/* Internal API
 * - action order from internal api documentation
 * - action property order from internal api documentation
 */

export interface NinchatGetQueueMetadataAction {
    action: 'get_queue_metadata'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    interval_begin?: NinchatProperties.Time
    interval_end?: NinchatProperties.Time
    metadata_keys?: Array<string>
}

export interface NinchatDeleteMessageAction extends NinchatPuppetActions {
    action: 'delete_message'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    message_id: NinchatProperties.MessageID
    user_id?: NinchatProperties.UserID // undocumented
}

// export interface NinchatUpdateSuggestionsAction extends NinchatPuppetActions {}

export interface NinchatRegisterAudienceAction extends NinchatPuppetActions {
    action: 'register_audience'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    audience_metadata: NinchatProperties.NinchatAudienceMetadata
    file_ids?: NinchatProperties.FileID[]
}

export interface NinchatEndAudienceAction extends NinchatPuppetActions {
    action: 'end_audience'
    action_id: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
}

export interface NinchatCreateCommentAction extends NinchatPuppetActions {
    action: 'create_comment'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
    comment_text: NinchatProperties.CommentText
}

export interface NinchatGetAudienceRangeCommentsAction extends NinchatPuppetActions {
    action: 'get_audience_range_comments'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    audience_first: NinchatProperties.AudienceID
    audience_last?: NinchatProperties.AudienceID
    history_length: NinchatProperties.Count
}

export interface NinchatBeginSsoAction extends NinchatPuppetActions {
    action: 'begin_sso'
    action_id?: NinchatProperties.ActionID // documented not optional
    sso_name?: NinchatProperties.SsoName
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
    access_url?: NinchatProperties.AccessUrl // documented not optional
    access_type?: NinchatProperties.AccessType
    access_url_params?: NinchatProperties.AccessUrlParams
    access_hpa?: NinchatProperties.AccessHpa
}

/* Undocumented API
 * - action order alphabetically
 * - action property order
 *   - id properties first alphabetically
 *   - rest of properties alphabetically
 */

// https://github.com/somia/nin/issues/2561#issuecomment-1857101164
export interface NinchatAssignOldestTranscriptAction extends NinchatPuppetActions {
    action: 'assign_oldest_transcript'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
}

// https://github.com/somia/nin/issues/2561#issuecomment-1857101164
export interface NinchatAssignTranscriptAction {
    action: 'assign_transcript'
    action_id: NinchatProperties.ActionID
    assign_user_id?: NinchatProperties.UserID
    audience_id: NinchatProperties.AudienceID
    unassign_user_id?: NinchatProperties.UserID
}

// Block the IP address of the customer user of an audience. Effect is realm-wide but temporary.
export interface NinchatBlockAudienceAddressAction {
    action: 'block_audience_address'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
}

// https://github.com/somia/nin/pull/2583#issuecomment-1904163696
export interface NinchatDescribeTranscriptAction {
    action: 'describe_transcript'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
}

export interface NinchatDiscoverJitsiAction {
    action: 'discover_jitsi'
    action_id: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
}

export interface NinchatGetQueueActivityAction {
    action: 'get_queue_activity'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
}

// https://github.com/somia/nin/issues/2561#issuecomment-1857101164
export interface NinchatSetTranscriptStatusAction {
    action: 'set_transcript_status'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
    transcript_status: NinchatProperties.TranscriptStatus
}

export interface NinchatGetPermissionGroupsAction {
    action: 'list_permission_groups'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.QueueID
}

export interface NinchatCreatePermissionGroupAction {
    action: 'create_permission_group'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.QueueID
    permission_group_attrs?: NinchatProperties.NinchatPermissionGroups[0]['permission_group_attrs']
    permissions?: Array<NinchatProperties.PermissionTypes>
}

export interface NinchatUpdatePermissionGroupAction {
    action: 'update_permission_group'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.QueueID
    permission_group_id: string
    permission_group_attrs?: NinchatProperties.NinchatPermissionGroups[0]['permission_group_attrs']
    permissions?: Array<NinchatProperties.PermissionTypes>
}

export interface NinchatDeletePermissionGroupAction {
    action: 'delete_permission_group'
    permission_group_id: string
    realm_id: NinchatProperties.QueueID
}

export interface NinchatJoinPermissionGroupAction {
    action: 'join_permission_group'
    action_id: NinchatProperties.ActionID
    permission_group_id: string
}

/* Unions */

export type NinchatAction =
    // Public API
    NinchatCreateSessionAction |
    // NinchatResumeSessionAction |
    NinchatUpdateSessionAction |
    NinchatCloseSessionAction |
    // NinchatCreateUserAction |
    NinchatDescribeUserAction |
    NinchatUpdateUserAction |
    NinchatDeleteUserAction |
    NinchatCreateIdentityAction |
    NinchatCreateIdentityWithAuthResetAccessAction |
    NinchatRequestIdentityVerifyAccessAction |
    NinchatVerifyIdentityAction |
    NinchatDescribeIdentityAction |
    NinchatUpdateIdentityAction |
    NinchatUpdateIdentityAuthAction |
    NinchatRequestIdentityAuthResetAccessAction |
    NinchatResetIdentityAuthAction |
    NinchatDeleteIdentityAction |
    NinchatUpdateDialogueAction |
    NinchatCreateChannelAction |
    NinchatDescribeChannelAction |
    NinchatUpdateChannelAction |
    // NinchatDeleteChannelAction |
    // NinchatFollowChannelAction |
    NinchatJoinChannelAction |
    NinchatPartChannelAction |
    // NinchatCreateRealmAction |
    NinchatDescribeRealmAction |
    NinchatDescribeRealmQueuesAction |
    NinchatDescribeRealmSitesAction |
    NinchatUpdateRealmAction |
    // NinchatDeleteRealmAction |
    NinchatCreateQueueAction |
    NinchatUpdateQueueAction |
    // NinchatDeleteQueueAction |
    NinchatDescribeQueueAction |
    NinchatDescribeSiteAction |
    NinchatDeleteSiteAction |
    NinchatUpdateSiteAction |
    NinchatCreateSiteAction |
    NinchatRequestAudienceAction |
    NinchatAcceptAudienceAction |
    NinchatTransferAudienceAction |
    NinchatAddMemberAction |
    NinchatUpdateMemberAction |
    NinchatRemoveMemberAction |
    NinchatSendMessageAction |
    NinchatUpdateMessageAction |
    NinchatUpdateUserMessagesAction |
    NinchatLoadHistoryAction |
    // NinchatDiscardHistoryAction |
    NinchatCreateAccessAction |
    NinchatSendAccessAction |
    NinchatDescribeAccessAction |
    NinchatDescribeMasterKeysAction |
    NinchatCreateMasterKeyAction |
    NinchatDeleteMasterKeyAction |
    NinchatSendFileAction |
    NinchatDescribeFileAction |
    NinchatGetTranscriptAction |
    // NinchatDeleteTranscriptAction |
    NinchatDescribeQueueTranscriptsAction |
    // NinchatDeleteQueueTranscriptsAction |
    NinchatCreateTagAction |
    NinchatDescribeTagAction |
    NinchatDescribeTagsAction |
    NinchatUpdateTagAction |
    NinchatDeleteTagAction |
    NinchatGetQueueStatsAction |
    NinchatGetQueueMetadataAction |
    // NinchatBeginIceAction |
    // NinchatSearchAction |
    // NinchatSearchUsersAction |
    // NinchatSearchChannelsAction |
    // NinchatTrackAction |
    // NinchatPingAction |
    // Internal API
    NinchatDeleteMessageAction |
    // NinchatUpdateSuggestionsAction |
    NinchatRegisterAudienceAction |
    NinchatEndAudienceAction |
    NinchatCreateCommentAction |
    NinchatGetAudienceRangeCommentsAction |
    NinchatBeginSsoAction |
    // Undocumented API
    NinchatAssignOldestTranscriptAction |
    NinchatAssignTranscriptAction |
    NinchatBlockAudienceAddressAction |
    NinchatDescribeTranscriptAction |
    NinchatDiscoverJitsiAction |
    NinchatGetQueueActivityAction |
    NinchatSetTranscriptStatusAction |
    NinchatGetPermissionGroupsAction |
    NinchatCreatePermissionGroupAction |
    NinchatJoinPermissionGroupAction |
    NinchatDeletePermissionGroupAction |
    NinchatUpdatePermissionGroupAction |
    NinchatDeleteQueueAction |
    NinchatSendFileUnlinkedAction |
    NinchatRemoveFileUnlinkedAction

// vim:foldmethod=syntax
